import { HmoQueryResponse, replyToHmoQuery } from "../apis/hmoQueryService";
import { QueryData } from "../interfaces/IPreAuthQuery";
import {
  getQueryPanelStageKey,
  isWorkflowStageNmiQuery,
  queryMatchesPanelStage,
} from "../components/patient/patient-details/newPatientDetailsConstants";
import { HMO_ROLE, isHmoAdmin, isHmoDoctorFamily, normalizeHmoRoleCode } from "./hmoRoles";
import {
  normalizeQueryReturnedDocuments,
  resolveQueryDocumentMasterName,
} from "./queryDocumentUtils";

export type QueryRespondFile = {
  file?: File;
  documentType?: string;
  documentId?: number;
};

export const isOpenQuery = (
  query: Pick<QueryData, "status" | "stateName" | "returnedResponse">,
) => {
  if (query.status !== true) return false;
  if (query.stateName === "Query Closed") return false;
  const state = String(query.stateName || "").toLowerCase();
  if (state.includes("replied")) return false;
  if (query.returnedResponse) return false;
  return true;
};

export const isStageStatusQuery = (statusKey?: string | null): boolean =>
  String(statusKey || "").toUpperCase() === "QUERY";

export const QUERY_REPLY_REQUIRED_MESSAGE =
  "Please reply to the open query before approve, reject, or NMI.";

export const QUERY_AWAITING_HOSPITAL_MESSAGE =
  "Waiting for the hospital to reply to the query before you can approve, reject, or raise NMI.";

export const QUERY_PENDING_HMO_REVIEW_MESSAGE =
  "Please approve or close the hospital's NMI response before approving the case or amount.";

const normalizeStageToken = (value: string): string =>
  value.trim().toLowerCase().replace(/[^a-z0-9]/g, "");

const isCmdCmoStageToken = (token: string): boolean =>
  token === "cmd" || token === "cmo";

export const queryStageMatchesLabel = (
  queryStage: string | undefined | null,
  stageLabel: string,
): boolean => {
  if (!queryStage || !stageLabel) return false;
  const queryToken = normalizeStageToken(queryStage);
  const stageToken = normalizeStageToken(stageLabel);
  if (isCmdCmoStageToken(queryToken) && isCmdCmoStageToken(stageToken)) {
    return true;
  }
  return (
    queryToken === stageToken ||
    queryToken.includes(stageToken) ||
    stageToken.includes(queryToken)
  );
};

export type OpenQueryCheck = Pick<
  QueryData,
  "status" | "stateName" | "returnedResponse" | "queryStage"
> & {
  casePreAuthId?: number | null;
};

export const hasOpenQueryForStageLabel = (
  queries: OpenQueryCheck[] | null | undefined,
  stageLabel: string,
): boolean =>
  (queries || []).some(
    (query) => isOpenQuery(query) && queryStageMatchesLabel(query.queryStage, stageLabel),
  );

export const hasOpenEnhancementQuery = (
  queries: OpenQueryCheck[] | null | undefined,
  enhancementId?: number | null,
): boolean =>
  (queries || []).some((query) => {
    if (!isOpenQuery(query)) return false;
    if (!queryStageMatchesLabel(query.queryStage, "Enhancement")) return false;
    if (enhancementId == null) return true;
    return Number(query.casePreAuthId) === Number(enhancementId);
  });

export const isQueryPendingHmoReview = (
  query: Pick<QueryData, "status" | "stateName" | "returnedResponse">,
): boolean => {
  if (query.status !== true) return false;
  const state = String(query.stateName || "").toLowerCase();
  if (state.includes("closed")) return false;
  return Boolean(String(query.returnedResponse || "").trim());
};

export const hasPendingHmoQueryReview = (
  queries: OpenQueryCheck[] | null | undefined,
): boolean => (queries || []).some((query) => isQueryPendingHmoReview(query));

export const canActOnRaisedQuery = (
  userRole?: string | null,
  raisedByRole?: string | null,
): boolean => {
  // Admin, Doctor, and HOD may act on any replied NMI in their visible list.
  if (isHmoAdmin(userRole) || isHmoDoctorFamily(userRole)) return true;
  if (!String(raisedByRole || "").trim()) return true;
  return (
    normalizeHmoRoleCode(userRole) === normalizeHmoRoleCode(raisedByRole)
  );
};

export const isHospitalPortalUser = (role?: string | null): boolean => {
  const normalized = normalizeHmoRoleCode(role);
  return (
    normalized === HMO_ROLE.HSP_USER || normalized === HMO_ROLE.HMO_PORTAL_USER
  );
};

export const getQueryDisplayRemarks = (
  query?: Pick<
    HmoQueryResponse,
    "updatedRemarks" | "receivedQuery" | "queryQuestion"
  > | null,
): string =>
  String(
    query?.updatedRemarks ||
      query?.receivedQuery ||
      query?.queryQuestion ||
      "",
  ).trim();

export const mapHmoQueryToQueryData = (query: HmoQueryResponse): QueryData => {
  const returnedDocumentUrl = normalizeQueryReturnedDocuments(query);
  const displayRemarks = getQueryDisplayRemarks(query);

  return {
    id: query.id,
    key: String(query.id),
    queryDateTime: query.createdAt ? String(query.createdAt) : "",
    queryReceived: displayRemarks,
    queryType: query.queryType || "",
    assignedTo: query.typeOfQuery || "",
    status: query.status,
    updateDateTime: query.updatedAt ? String(query.updatedAt) : "",
    responseTime: query.responseDateTime ? String(query.responseDateTime) : "",
    recievedDocumentUrl: returnedDocumentUrl,
    returnedDocumentUrl,
    queryRequiredDocuments: query.queryRequiredDocuments,
    oldDocumentId: query.oldDocumentId,
    newDocumentId: query.newDocumentId,
    stageStateDto: null,
    returnedResponse: query.returnedResponse || "",
    receivedQuery: displayRemarks,
    updatedRemarks: query.updatedRemarks,
    stateName:
      query.stateName ||
      (query.returnedResponse ? "NMI Replied" : undefined),
    updatedAt: query.updatedAt ? String(query.updatedAt) : undefined,
    typeOfQuery: query.typeOfQuery,
    stageId: query.stageId,
    queryStage: query.queryStage,
    deficiencyQuery: query.deficiencyQuery,
    queryKind: query.queryKind,
    casePreAuthId: query.casePreAuthId,
    raisedByRole: query.raisedByRole,
    oldDocument: query.oldDocumentId
      ? {
          id: query.oldDocumentId,
          documentLabelType: "Document",
          documentType: "OTHER",
          documentUrl: query.returnedDocumentUrl,
        }
      : undefined,
  } as QueryData;
};

/** Group queries under QUERY sidebar panels using query_stage string (not visitStageId). */
export const groupQueriesByQueryStage = (
  queries: HmoQueryResponse[],
  visitStages?: Array<Record<string, any>> | null,
): Record<string, QueryData[]> => {
  const grouped: Record<string, QueryData[]> = {};
  const panels = visitStages || [];

  for (const panel of panels) {
    const key = getQueryPanelStageKey(panel);
    if (key && !grouped[key]) {
      grouped[key] = [];
    }
  }

  for (const query of queries) {
    const mapped = mapHmoQueryToQueryData(query);
    let placed = false;

    for (const panel of panels) {
      const key = getQueryPanelStageKey(panel);
      if (!key || !queryMatchesPanelStage(query.queryStage, panel)) continue;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(mapped);
      placed = true;
      break;
    }

    if (!placed && query.queryStage) {
      if (isWorkflowStageNmiQuery(query.queryStage)) {
        const claimPanel = panels.find((panel) =>
          queryMatchesPanelStage(query.queryStage, panel),
        );
        if (claimPanel) {
          const claimKey = getQueryPanelStageKey(claimPanel);
          if (claimKey) {
            if (!grouped[claimKey]) grouped[claimKey] = [];
            grouped[claimKey].push(mapped);
            placed = true;
          }
        }
      }

      if (!placed) {
        const fallbackKey = getQueryPanelStageKey({
          visitStage: query.queryStage,
        });
        if (!grouped[fallbackKey]) grouped[fallbackKey] = [];
        grouped[fallbackKey].push(mapped);
      }
    }
  }

  return grouped;
};

/** @deprecated Use groupQueriesByQueryStage */
export const groupQueriesByStageId = groupQueriesByQueryStage;

export const submitHmoQueryReply = async (
  visitId: number | string,
  queryId: number | string,
  remarks: string,
  files: QueryRespondFile[] = [],
  options?: { newDocumentId?: number; oldDocumentId?: number },
) => {
  const documentIds = files
    .map((item) => item.documentId)
    .filter((id): id is number => id != null && id > 0);

  const uploadFiles = files
    .filter((item) => item.file && !item.documentId)
    .map((item) => ({
      file: item.file as File,
      documentType: resolveQueryDocumentMasterName(
        item.documentType || "OTHER",
      ),
    }));

  const isDeficiencyReply = options?.oldDocumentId != null;
  const newDocumentId =
    options?.newDocumentId ??
    (isDeficiencyReply && documentIds.length === 1
      ? documentIds[0]
      : undefined);

  return replyToHmoQuery(visitId, queryId, {
    returnedResponse: remarks,
    ...(newDocumentId != null ? { newDocumentId } : {}),
    ...(options?.oldDocumentId != null
      ? { oldDocumentId: options.oldDocumentId }
      : {}),
    ...(!isDeficiencyReply && documentIds.length
      ? { returnedDocumentIds: documentIds }
      : {}),
    ...(uploadFiles.length ? { files: uploadFiles } : {}),
  });
};
